import { Box, IconButton, Typography } from "@mui/material";
import {
  Button,
  Chips,
  Color,
  Modal,
  SimpleInput,
  Toast,
} from "@axismaxlife/unified-ui-components-library";
import { CloseIcon } from "@axismaxlife/unified-ui-components-library/dist/systemIcons";
import { useEffect, useState } from "react";
import { CoursesArray } from "../constants/CoursesArray";
import { initialCourseValues } from "../constants/CoursesInitialValues";
import { String } from "../constants/Strings";
import { amountInWords } from "../logic/NumberToWord";
import { toAlphabets } from "../logic/CommonFunctions";
import { colors } from "../style/color/colors";
import { fonts } from "../style/fonts/fonts";
import { YearInput } from "./YearInput";
import { AppToast } from "./AppToast";

const OTHER_COURSE = "Others";

const EMPTY_CHILD = {
  name: "",
  age: "",
  dreamCourse: "",
  courseName: "",
  rangeOfCourse: "",
  educationYear: "",
  weddingBudget: "",
  weddingYear: "",
};

const averageCostHint = (selectedCourse) => {
  if (!selectedCourse) return "";
  const cost = `${initialCourseValues[selectedCourse] ?? initialCourseValues[OTHER_COURSE] ?? ""}`.trim();
  if (!cost) return "";
  const label = selectedCourse === OTHER_COURSE ? "Other courses" : selectedCourse;
  return `${String.ChildPlannig.avgCostHint}`
    .replace("{course}", label)
    .replace("{cost}", cost);
};

const toDigits = (raw) => `${raw ?? ""}`.replace(/[^0-9]/g, "");
const toText = (raw) => (raw === null || raw === undefined ? "" : `${raw}`);

const toFormState = (value) =>
  value
    ? {
      name: toText(value.name),
      age: toText(value.age),
      dreamCourse: toText(value.dreamCourse),
      courseName: toText(value.courseName),
      rangeOfCourse: toText(value.rangeOfCourse),
      educationYear: toText(value.educationYear),
      weddingBudget: toText(value.weddingBudget),
      weddingYear: toText(value.weddingYear),
    }
    : EMPTY_CHILD;

export const ChildDetailsModal = ({
  isOpen,
  onClose,
  onSave,
  initialValue,
}) => {
  const [child, setChild] = useState(EMPTY_CHILD);
  const [formKey, setFormKey] = useState(0);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastKey, setToastKey] = useState(0);

  const showNameError = () => {
    setToastOpen(true);
    setToastKey((k) => k + 1);
  };

  useEffect(() => {
    if (isOpen) {
      setChild(toFormState(initialValue));
      setFormKey((key) => key + 1);
      setToastOpen(false);
    }
  }, [isOpen]);

  const setField = (field, value) =>
    setChild((prev) => ({ ...prev, [field]: value }));

  const setAmount = (field) => (e) => setField(field, toDigits(e.target.value));
  const setAge = (e) =>
    setField("age", toDigits(e.target.value).slice(0, 3));

  const isOtherCourse = child.dreamCourse === OTHER_COURSE;

  const isValid = Boolean(
    `${child.name}`.trim() &&
    child.age &&
    child.dreamCourse &&
    (!isOtherCourse || `${child.courseName}`.trim()) &&
    child.rangeOfCourse &&
    child.educationYear &&
    child.weddingBudget &&
    child.weddingYear,
  );

  const handleSave = () => {
    onSave({
      ...child,
      name: `${child.name}`.trim(),
      age: Number(child.age),
      courseName: isOtherCourse ? `${child.courseName}`.trim() : "",
      rangeOfCourse: Number(child.rangeOfCourse),
      educationYear: Number(child.educationYear),
      weddingBudget: Number(child.weddingBudget),
      weddingYear: Number(child.weddingYear),
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      device="mobile"
      variant="bottom sheet"
      showCloseIcon
      className="acr-details-modal"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: "20px",
              fontFamily: fonts.lato,
              fontWeight: 700,
              color: colors.NeutralDark700,
            }}
          >
            {String.ChildPlannig.enterChildDetails}
          </Typography>
          <IconButton
            sx={{ p: 0, flexShrink: 0 }}
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon size="small" fillColor={Color.Icons.Default} />
          </IconButton>
        </Box>
        <SimpleInput
          name="childName"
          width="100%"
          label={String.ChildPlannig.namePlaceholder}
          placeholder={String.ChildPlannig.namePlaceholder}
          value={child.name}
          onChange={(e) => setField("name", toAlphabets(e.target.value, showNameError))}
        />
        <SimpleInput
          name="childAge"
          type="text"
          inputMode="numeric"
          width="100%"
          label={String.ChildPlannig.agePlaceholder}
          placeholder={String.ChildPlannig.agePlaceholder}
          value={child.age}
          onChange={setAge}
        />

        <Box>
          <Typography
            sx={{
              mb: "8px",
              fontSize: "16px",
              fontFamily: fonts.manrope,
              color: colors.NeutralDark700,
            }}
          >
            {String.ChildPlannig.selectDreamCourse}
          </Typography>
          <Chips
            key={formKey}
            device="mobile"
            layout="multipleRows"
            items={CoursesArray.map((course) => ({ label: course }))}
            defaultSelected={child.dreamCourse ? [child.dreamCourse] : []}
            onChange={(selectedChips) => {
              const chosen = (selectedChips || []).find(
                (chip) => chip.selected,
              );
              setField("dreamCourse", chosen ? chosen.label : "");
            }}
          />
        </Box>

        {child.dreamCourse && (
          <Toast
            device="mobile"
            state="information"
            width="100%"
            closeIcon={false}
            title={averageCostHint(child.dreamCourse)}
          />
        )}

        {isOtherCourse && (
          <SimpleInput
            name="courseName"
            width="100%"
            label={String.ChildPlannig.courseNamePlaceholder}
            placeholder={String.ChildPlannig.courseNamePlaceholder}
            value={child.courseName}
            onChange={(e) => setField("courseName", toAlphabets(e.target.value, showNameError))}
          />

        )}

        <SimpleInput
          name="educationCost"
          type="number"
          indianNumberFormat
          prefix="₹"
          width="100%"
          label={String.ChildPlannig.educationCost}
          placeholder={String.ChildPlannig.educationCost}
          value={
            child.rangeOfCourse === 0 || child.rangeOfCourse === "0" || !child.rangeOfCourse
              ? ""
              : child.rangeOfCourse
          }
          hintText={amountInWords(child.rangeOfCourse)}
          onChange={setAmount("rangeOfCourse")}
        />
        <YearInput
          name="educationYear"
          label={String.ChildPlannig.educationFundsBy}
          value={child.educationYear}
          onChange={(digits) => setField("educationYear", digits)}
        />
        <SimpleInput
          name="weddingCost"
          type="number"
          indianNumberFormat
          prefix="₹"
          width="100%"
          label={String.ChildPlannig.weddingCost}
          placeholder={String.ChildPlannig.weddingCost}
          value={
            child.weddingBudget === 0 || child.weddingBudget === "0" || !child.weddingBudget
              ? ""
              : child.weddingBudget
          }
          hintText={amountInWords(child.weddingBudget)}
          onChange={setAmount("weddingBudget")}
        />
        <YearInput
          name="weddingYear"
          label={String.ChildPlannig.weddingFundsBy}
          value={child.weddingYear}
          onChange={(digits) => setField("weddingYear", digits)}
        />

        <Box sx={{ mt: "8px" }}>
          <Button
            buttonType="primary"
            size="default"
            width="100%"
            disabled={!isValid}
            onClick={handleSave}
          >
            {String.ChildPlannig.saveDetails}
          </Button>
        </Box>
        <AppToast
          key={toastKey}
          open={toastOpen}
          message="Name cannot be a number"
          severity="error"
          handleClose={() => setToastOpen(false)}
        />
      </Box>
    </Modal>
  );
};
